import React, { Suspense, lazy, useEffect, useRef, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import AccessDialog from "./AccessDialog";
import { getDialogOpen } from "./app/appSlice";
import { getAuthorized, getUser, logout } from "./app/authSlice";
import { getCouncilInfo, getUsersAccessControl } from "./Content/Settings/common/FetchSettingsData";
import { getSubData, getSubModules, getUserCouncil, setSubData, setUserCouncil } from "./app/adminSlice";
import axios from "axios";
import { getGisSubModule } from "./Content/MainGIS/common/FetchMenuData";
import { getGuestLogin } from "./Login/FetchUserAuth";
import SuccessSnackbar from "./SuccessSnackbar";
import { useWebSocket, useWebSocketStatus } from "./utils/useWebSocket";
import { enqueueSnackbar } from "notistack";

// Lazy loading the components
const Main = lazy(() => import("./Main"));
const Gis = lazy(() => import("./Gis"));
const Dashboard = lazy(() => import("./Dashboard"));
const SmartDashboard = lazy(() => import("./Content/SmartDashboard"));
const Login = lazy(() => import("./Login"));
const MainGIS = lazy(() => import("./Content/MainGIS"));
const SmartGIS = lazy(() => import("./Content/SmartGIS"));
const Settings = lazy(() => import("./Settings"));
const SmartSettings = lazy(() => import("./Content/Settings"));
const UserProfile = lazy(() => import("./UserProfile"));

function App() {
  //Access controls
  const dispatch = useDispatch();
  const councils = useSelector(getUserCouncil);
  let council = null;
  if (councils) {
    council = councils[0];
  }
  const submodules = useSelector(getSubModules);
  const isAuthorized = useSelector(getAuthorized);
  const user = useSelector(getUser);
  const subData = useSelector(getSubData);
  const isIntercept = useRef(false);
  const [isAlert, setIsAlert] = useState(false);
  const isAlerted = useRef(false);
  const { connect, lastMessage } = useWebSocket();
  const isFirstRender = useRef(true);
  const [playing, setPlaying] = useState(false);
  const [audio] = useState(() => new Audio("/sound/alert.wav"));
  const alertTimerRef = useRef(null);

  function InitializeAccess() {
    useEffect(() => {
      //call access controls for the current user.
      if (!subData && isAuthorized) {
        getUsersAccessControl(dispatch, 1, user.user_id);
        getCouncilInfo(dispatch, user.council_id); //get user's council.
      } else if (!isAuthorized) {
        getGuestLogin(dispatch);
      }
    }, [user, isAuthorized, councils]);
  }

  useEffect(() => {
    if (!playing) return;

    let counter = 0;
    let cancelled = false;

    const playSafely = () => {
      audio.currentTime = 0;
      const playPromise = audio.play();
      if (playPromise !== undefined) {
        playPromise.catch((err) => {
          // Ignore AbortError from a pause() interrupting play() —
          // anything else, surface it.
          if (err.name !== "AbortError") {
            console.error("Audio play failed:", err);
          }
        });
      }
    };

    playSafely(); // play immediately, then every interval

    alertTimerRef.current = setInterval(() => {
      counter += 1;
      if (counter > 3) {
        clearInterval(alertTimerRef.current);
        audio.pause();
        audio.currentTime = 0;
        if (!cancelled) setPlaying(false);
        return;
      }
      playSafely();
    }, 1000);

    // cleanup: runs on unmount AND whenever `playing` changes
    return () => {
      cancelled = true;
      clearInterval(alertTimerRef.current);
      audio.pause();
      audio.currentTime = 0;
    };
  }, [playing, audio]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (lastMessage?.status === "triggered") {
      enqueueSnackbar(`Panic Button for ${lastMessage?.postName} is Triggered`, {
        variant: "variant",
        anchorOrigin: { vertical: "bottom", horizontal: "right" },
        autoHideDuration: 3000,
      });
      setPlaying(true);
    }
  }, [lastMessage?.postId]);

  useEffect(() => {
    connect("wss://api7.sdev.my:7443/v1/api/emergency/ws/map/emergencies", {
      reconnectInterval: 5000,
      maxReconnectAttempts: 10,
    });

    getGisSubModule(dispatch); //get submodule

    if (!isIntercept.current) {
      // Set the interceptor only once after error caught
      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (error?.response) {
            const status = error?.response?.status;

            if (status === 401 && !isAlerted.current) {
              try {
                // Only show alert once
                // setIsAlert(true);
                isAlerted.current = true;
                alert("Tempoh Anda Sudah Tamat, Sila Log Masuk Kembali");

                // Dispatch logout and clear session storage
                dispatch(logout());
                sessionStorage.removeItem("user");
                sessionStorage.removeItem("token");
                sessionStorage.removeItem("time");
                sessionStorage.removeItem("expandedModules");
                dispatch(setSubData(null));
                dispatch(setUserCouncil(null));
                window.location.href = "/"; // Redirect to landing page
              } catch (err) {
                console.error("Error handling 401:", err);
              }
            }

            // Reject the error to ensure proper error handling in the axios call chain
            return Promise.reject(error);
          } else {
            console.error("No Response from Server:", error?.message);
            return Promise.reject(error);
          }
        },
      );

      // Prevent re-setting the interceptor
      isIntercept.current = true;
    }
  }, []);

  // set the title based on config.js
  document.title = council?.website_name || window.appTheme.websiteName;

  // the version number derived from .env file
  window.appTheme.version = process.env.REACT_APP_VERSION;
  console.log(window.appTheme.version);

  //find submodules where it corresponds with what users have access to (because i need the submodule_link field)
  const accessMod = submodules?.filter((sub) => subData?.some((access) => sub?.submodule_id === access?.submodule_id));

  // Component to set custom header based on route, and a background setter based on current route. (Had to put it here caz it was convenient)
  function CustomHeaderSetter() {
    const location = useLocation();

    useEffect(() => {
      //background picker =================================================================================================================
      if (council) {
        if (isAuthorized) {
          //remove 'time' and 'expandedModules' ss, time indicator log sistem & selected mods access contr==============================
          if (!location.pathname.includes("kawalan_capaian")) {
            if (sessionStorage.getItem("expandedModules")) {
              sessionStorage.removeItem("expandedModules");
            }
          }
          if (!location.pathname.includes("log_sistem")) {
            if (sessionStorage.getItem("time")) {
              sessionStorage.removeItem("time");
            }
          }
          //=========================================================================

          if (location.pathname.includes("settings")) {
            document.body.style.backgroundImage = "none";
            document.body.style.backgroundColor = "#2b2a2a";
          } else {
            if ((document.body.style.backgroundImage = "none")) {
              document.body.style.backgroundImage = `url(${window.appConfig.mediaUrl}/${council.background})`;
              document.body.style.backgroundColor = "none";
            }
          }
        }
      } else {
        if (!isAuthorized && document.body.style.backgroundImage !== `url(${window.appTheme.background})`) {
          document.body.style.backgroundImage = `url(${window.appTheme.background})`;
        }
        //Unautorhized users/guests restricted access to certain modules==========================================
        if (!isAuthorized) {
          if (location.pathname.includes("settings") || location.pathname.includes("dashboard") || location.pathname.includes("profil_pengguna")) {
            window.location.href = "/";
          }
        } else {
          if (location.pathname.includes("dashboard") || location.pathname.includes("settings")) {
            const accessible = accessMod?.some((acc) => acc?.submodule_link === location.pathname);
            if (!accessible) {
              window.location.href = "/";
            }
          }
        }
        //========================================================================================================
      }
      //===================================================================================================================================

      const token = sessionStorage.getItem("token");
      if (token) {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
      }
      axios.defaults.headers.common["X-Frontend-Path"] = location.pathname;
    }, [location, council]);

    return null;
  }

  //dialog
  const dialogOpen = useSelector(getDialogOpen);

  return (
    <>
      <Router>
        {/* Iterate interceptor & Get User's Access Control */}
        <CustomHeaderSetter />
        <InitializeAccess />
        {/* Suspense fallback while lazy-loaded components are being loaded */}
        <Suspense fallback={<div>Loading...</div>}>
          <Routes>
            <Route path="profil_pengguna" element={<UserProfile page="SmartProfile" />} />
            <Route path="settings" element={<Settings />}>
              <Route index element={<SmartSettings key="main" page="SmartSettings" />} />
              <Route path="pengurusan_pengguna" element={<SmartSettings key="pengurusan_pengguna" page="SmartSettings" panel="UsersList" />} />
              <Route path="kawalan_capaian" element={<SmartSettings key="kawalan_capaian" page="SmartSettings" panel="AccessControl" />} />
              <Route path="log_sistem" element={<SmartSettings key="log_sistem" page="SmartSettings" panel="SystemLogs" />} />
              <Route path="kemaskini_majlis" element={<SmartSettings key="kemaskini_majlis" page="SmartSettings" panel="EditCouncil" />} />
            </Route>
            <Route path="/" element={<Main />} />
            <Route path="gis" element={<Gis />}>
              <Route index element={<MainGIS key="main" page="MainGIS" gisModule="MainGIS" />} />
              <Route path="rt" element={<SmartGIS key="rt" page="SmartGIS" gisModule="RancanganTempatan" />} />
              <Route path="rtdoc" element={<SmartGIS key="rtdoc" page="RTDoc" gisModule="RancanganTempatanDoc" withSideContent={false} />} />
              <Route path="kawasan_lapang" element={<SmartGIS key="kawasan_lapang" page="SmartGIS" gisModule="KawasanLapang" />} />
              <Route path="mpp" element={<SmartGIS key="mpp" page="SmartGIS" gisModule="MPP" />} />
              <Route path="pembersihan_awam" element={<SmartGIS key="pembersihan_awam" page="SmartGIS" gisModule="PembersihanAwam" />} />
              <Route path="sisa_pepejal" element={<SmartGIS key="sisa_pepejal" page="SmartGIS" gisModule="SisaPepejal" />} />
              <Route path="kemudahan_masyarakat" element={<SmartGIS key="kemudahan_masyarakat" page="SmartGIS" gisModule="KemudahanMasyarakat" />} />
              <Route path="pasar_gerai" element={<SmartGIS key="pasar_gerai" page="SmartGIS" gisModule="PasarGerai" />} />
            </Route>
            <Route path="/logout" element={<Navigate to="/" />} />
            <Route path="/login" element={<Login />} />

            <Route path="dashboard" element={<Dashboard />}>
              <Route
                index
                element={<SmartDashboard key="main" page="SmartDashboard" superModule="EkonomiPintar" mainModule="Hasil" dashboardModule="Utama" />}
              />
              <Route
                path="taksiran"
                element={
                  <SmartDashboard key="taksiran" page="SmartDashboard" superModule="EkonomiPintar" mainModule="Hasil" dashboardModule="Taksiran" />
                }
              />
              <Route
                path="perlesenan"
                element={
                  <SmartDashboard
                    key="perlesenan"
                    page="SmartDashboard"
                    superModule="EkonomiPintar"
                    mainModule="Hasil"
                    dashboardModule="Perlesenan"
                  />
                }
              />
              <Route
                path="letak_kereta"
                element={
                  <SmartDashboard
                    key="letak_kereta"
                    page="SmartDashboard"
                    superModule="EkonomiPintar"
                    mainModule="Hasil"
                    dashboardModule="LetakKereta"
                  />
                }
              />
              <Route
                path="mpp"
                element={
                  <SmartDashboard
                    key="dashboard_mpp"
                    page="SmartDashboard"
                    superModule="Masyarakat"
                    mainModule="MajlisPerwakilanPenduduk"
                    dashboardModule="mpp"
                  />
                }
              />
              <Route
                path="kemudahan_masyarakat"
                element={
                  <SmartDashboard
                    key="dashboard_kemudahan_masyarakat"
                    page="SmartDashboard"
                    superModule="Perbandaran"
                    mainModule="PetaKemudahanMasyarakat"
                    dashboardModule="kemudahan_masyarakat"
                  />
                }
              />
              <Route
                path="sisa_pepejal"
                element={
                  <SmartDashboard
                    key="dashboard_sisa_pepejal"
                    page="SmartDashboard"
                    superModule="AlamSekitar"
                    mainModule="SisaPepejal"
                    dashboardModule="sisa_pepejal"
                  />
                }
              />
              <Route
                path="pembersihan_awam"
                element={
                  <SmartDashboard
                    key="dashboard_pembersihan_awam"
                    page="SmartDashboard"
                    superModule="AlamSekitar"
                    mainModule="PembersihanAwam"
                    dashboardModule="pembersihan_awam"
                  />
                }
              />
              <Route
                path="pemantauan"
                element={<SmartDashboard key="pemantauan" page="SmartDashboard" mainModule="Pemantauan" dashboardModule="InfoBanjir" />}
              />
              <Route
                path="srams"
                element={<SmartDashboard key="srams" page="SmartDashboard" superModule="Infrastruktur" mainModule="SRAMS" dashboardModule="SRAMS" />}
              />
              <Route
                path="CERUN"
                element={<SmartDashboard key="cerun" page="SmartDashboard" superModule="Infrastruktur" mainModule="CERUN" dashboardModule="CERUN" />}
              />
              <Route
                path="rt"
                element={
                  <SmartDashboard
                    key="dashboard_rt"
                    page="SmartDashboard"
                    superModule="PerkhidmatanAwam"
                    mainModule="OpenData"
                    dashboardModule="rt"
                  />
                }
              />
              <Route
                path="doc"
                element={
                  <SmartDashboard
                    key="dashboard_doc"
                    page="SmartDashboard"
                    superModule="PerkhidmatanAwam"
                    mainModule="OpenData"
                    dashboardModule="doc"
                  />
                }
              />
              <Route
                path="kawasan_lapang"
                element={
                  <SmartDashboard
                    key="dashboard_kawasan_lapang"
                    page="SmartDashboard"
                    superModule="PerkhidmatanAwam"
                    mainModule="OpenData"
                    dashboardModule="kawasan_lapang"
                  />
                }
              />
              <Route
                path="panic_button"
                element={
                  <SmartDashboard
                    key="dashboard_panic_button"
                    page="SmartDashboard"
                    superModule="Perbandaran"
                    mainModule="PanicButton"
                    dashboardModule="panic_button"
                  />
                }
              />
            </Route>
          </Routes>
        </Suspense>
      </Router>

      {dialogOpen && <AccessDialog dispatch={dispatch} />}
      <SuccessSnackbar />
    </>
  );
}

export default App;
